import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OFF_STAGE_ROUTE } from '../components/empathy/callRoutes';
import { isFullScreenStageOpen } from '../components/stage/fullScreenStage';
import { deepLinkService } from '../services/deeplink.service';
import { networkService } from '../services/network.service';
import { safeAreaService } from '../services/safearea.service';
import { useAuthStore } from '../stores/auth.store';
import { isCallSessionLive, useEmpathyQueueStore } from '../stores/empathy-queue.store';
import { closeOpenGate } from './useGateOpen';
import { closeTopModalLayer } from '../utils/modal-stack';

/**
 * Router-level bootstrap hooks. Previously these were 5 inline `useEffect`
 * blocks in AppRouter (app.router.tsx) with a TODO asking for extraction
 * for testability. Each hook here owns one concern.
 *
 * Hooks:
 *   - useAuthInit — calls initAuth() once on mount.
 *   - useAppServices — deep links, safe area, network; tears down cleanly.
 *   - useNetworkStatus — returns live isOffline state + retry handler.
 *   - useOrientationSafeArea — re-reads safe-area insets on rotation.
 *   - useNativeBackButton — maps Capacitor back-button to router navigation.
 */

export function useAuthInit(): void {
  const initAuth = useAuthStore((state) => state.initAuth);
  useEffect(() => {
    void initAuth();
  }, [initAuth]);
}

export function useAppServices(): void {
  const navigate = useNavigate();
  useEffect(() => {
    deepLinkService.initialize(navigate);
    void safeAreaService.initialize();
    void networkService.initialize();

    const authStore = useAuthStore.getState();
    authStore.initializeDeepLinks();

    return () => {
      const cleanupAuthStore = useAuthStore.getState();
      cleanupAuthStore?.cleanupDeepLinks();
      networkService?.destroy();
    };
  }, [navigate]);
}

export function useNetworkStatus(): { isOffline: boolean; retry: () => Promise<boolean> } {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const unsubscribe = networkService.addListener((isOnline: boolean) => {
      setIsOffline(!isOnline);
    });

    const networkState = networkService.getNetworkState();
    setIsOffline(!networkState.isOnline);

    return unsubscribe;
  }, []);

  const retry = useCallback(async () => {
    const isOnline = await networkService.forceCheck();
    setIsOffline(!isOnline);
    return isOnline;
  }, []);

  return { isOffline, retry };
}

export function useOrientationSafeArea(): void {
  useEffect(() => {
    const handleOrientationChange = () => {
      safeAreaService.handleOrientationChange();
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);
}

/**
 * BACK, WHEREVER IT COMES FROM — and it never ends a call.
 *
 * THE BUG (a real phone, 2026-09-14). Back while on the 1:1 call stage ended
 * the call. Half of that was the stage's own unmount sending a `hangup`, which
 * is fixed in `utils/liveCall.ts`; the other half is here. `navigate(-1)` goes
 * to whatever happens to be behind, and behind a call the flow auto-navigated
 * to, that is as likely to be the empathy page again as anywhere — so Back
 * either did nothing visible or dropped out of a screen that was quietly
 * hanging up as it went. Neither is "minimise to the pill".
 *
 * THE RULE. While a full-screen stage is up over a LIVE call, Back leaves the
 * stage for a known page and the floating pill picks the call up; the call
 * itself is untouched, because the only ends are the End button and the
 * pill's. Once the call is over — the feedback screen is a full-screen surface
 * too — there is nothing to minimise to, so Back is ordinary history back
 * again, except over the post-call prompt ("Got the empathy you needed?",
 * "Thank you for listening", "{name} didn't pick up"), which is a dialog
 * answered with its buttons only and swallows Back (below).
 * `isCallSessionLive` is the one predicate that draws the live-call line, and
 * the pill's render guard asks the same one (class D).
 *
 * DIALOGS FIRST (#1159 backlog). Back is Android's Escape: the top RENDERED
 * dialog on the modal stack (utils/modal-stack.ts) answers it with its own
 * dismiss, and the page behind stays. A dialog that must be answered with
 * its buttons swallows Back: the post-call prompt, the ping modal, an
 * incoming call while it rings, the ring sheet (Back never declines a ring,
 * #1161 review), a confirm that is not dismissable. A dialog in a hidden
 * subtree (the 1:1 tab behind the call stage) is skipped, so Back on a live
 * call still leaves the stage.
 *
 * ONE BRANCH, BOTH BACKS. Android's hardware/gesture Back arrives as
 * Capacitor's `backButton`; a browser's arrives as `popstate`. They are two
 * events into one handler rather than two handlers, or the phone and the web
 * are free to disagree about what Back means.
 */
export function useNativeBackButton(): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    /**
     * Is a full-screen call stage on the screen with a live call behind it?
     * Read at the moment of the press, never off the render that registered
     * this listener — the call can end while the handler is installed.
     */
    const leavesTheStage = (): boolean =>
      isFullScreenStageOpen() && isCallSessionLive(useEmpathyQueueStore.getState());

    const handleBackButton = () => {
      // Back is Android's Escape: the rendered dialog on top answers it (its
      // own dismiss; a dialog that must be answered swallows it), and the
      // page behind stays where it is (utils/modal-stack.ts, #1159 backlog).
      if (closeTopModalLayer()) return;
      // A gate (norms, agreement, upgrade prompt) is on top of everything:
      // Back closes it and stays put, declining the parked request (P5-1).
      // The gates are on the stack above when mounted; this is the fallback
      // for a gate whose dialog is not (yet) on screen.
      if (closeOpenGate()) return;
      if (leavesTheStage()) {
        void navigate(OFF_STAGE_ROUTE);
        return;
      }
      // Exit app if on app root or public routes; otherwise go back.
      if (
        location.pathname === '/app' ||
        location.pathname === '/' ||
        ['/login', '/register'].includes(location.pathname)
      ) {
        void App.exitApp();
        return;
      }
      void navigate(-1);
    };

    /**
     * The browser's Back. The router handles the history entry itself; all
     * this adds is the stage rule, so a `popstate` that would have landed back
     * on the call page lands off the stage instead. Every other `popstate` is
     * left entirely alone.
     */
    const handlePopState = () => {
      if (!leavesTheStage()) return;
      void navigate(OFF_STAGE_ROUTE);
    };
    window.addEventListener('popstate', handlePopState);

    // addListener returns a Promise<PluginListenerHandle>. Removing via the
    // handle is the correct scoped teardown — NOT App.removeAllListeners(),
    // which nukes every listener on the App plugin (push notifications,
    // app-state, deep links, etc). Since this effect re-runs on every
    // location change, using the bulk remove would wipe other global
    // listeners repeatedly. This bug was pre-existing in the inline
    // useEffect in app.router.tsx and preserved during the T2 extraction;
    // fixed here per PR #275 gemini review.
    let handle: PluginListenerHandle | undefined;
    let cancelled = false;
    void App.addListener('backButton', handleBackButton).then((h) => {
      if (cancelled) {
        void h.remove();
        return;
      }
      handle = h;
    });

    return () => {
      cancelled = true;
      window.removeEventListener('popstate', handlePopState);
      void handle?.remove();
    };
  }, [navigate, location]);
}
